import {
  Dropdown as AntDropdown,
  DropdownProps as AntDropdownProps,
  theme,
} from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import { ReactNode, ReactElement } from 'react';
import type { GlobalToken } from 'antd';
import { radius } from '../../theme/radius';

export type DropdownPlacement =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight';

export type DropdownTrigger = 'click' | 'hover' | 'contextMenu';

export interface DropdownMenuItem {
  /** Unique key for the item */
  key: string;
  /** Label to display */
  label: ReactNode;
  /** Icon to display before label */
  icon?: ReactNode;
  /** Whether item is disabled */
  disabled?: boolean;
  /** Whether item is a danger action */
  danger?: boolean;
  /** Click handler */
  onClick?: () => void;
}

export interface DropdownMenuDivider {
  type: 'divider';
}

export interface DropdownMenuGroup {
  type: 'group';
  label: ReactNode;
  children: DropdownMenuItem[];
}

export type DropdownMenuItemType =
  | DropdownMenuItem
  | DropdownMenuDivider
  | DropdownMenuGroup;

export interface DropdownProps extends Omit<
  AntDropdownProps,
  'menu' | 'trigger'
> {
  /** Menu items to display */
  items: DropdownMenuItemType[];
  /** Element that triggers the dropdown */
  children: ReactElement;
  /** How to trigger the dropdown */
  trigger?: DropdownTrigger | DropdownTrigger[];
  /** Placement of dropdown */
  placement?: DropdownPlacement;
  /** Whether dropdown is disabled */
  disabled?: boolean;
  /** Callback when menu item is clicked */
  onItemClick?: (key: string) => void;
}

// Global styles for dropdown menu (rendered in portal)
// Note: Uses CSS variables from Ant Design ConfigProvider
const DropdownStyles = createGlobalStyle<{ $token: GlobalToken }>`
  .libertify-dropdown-menu.ant-dropdown-menu {
    padding: 0;
    border-radius: ${radius.box}px;
    box-shadow: ${({ $token }) => $token.boxShadowSecondary};
    border: ${({ $token }) =>
      `${$token.lineWidth}px solid ${$token.colorBorderSecondary}`};
    overflow: hidden;

    .ant-dropdown-menu-item {
      margin: 0;
      padding: ${({ $token }) => `${$token.paddingSM}px ${$token.padding}px`};
      border-radius: 0;
      font-family: ${({ $token }) => $token.fontFamily};
      font-size: ${({ $token }) => $token.fontSizeSM}px;
      color: ${({ $token }) => $token.colorText};
      transition: background ${({ $token }) => $token.motionDurationFast};

      &:hover:not(.ant-dropdown-menu-item-disabled) {
        background: ${({ $token }) => $token.colorBgLayout};
      }

      &.ant-dropdown-menu-item-disabled {
        color: ${({ $token }) => $token.colorTextDisabled};
        cursor: not-allowed;
      }

      &.ant-dropdown-menu-item-danger {
        color: ${({ $token }) => $token.colorError};

        &:hover:not(.ant-dropdown-menu-item-disabled) {
          background: ${({ $token }) => $token.colorErrorBg};
          color: ${({ $token }) => $token.colorError};
        }
      }
    }

    .ant-dropdown-menu-item-icon {
      margin-right: ${({ $token }) => $token.marginXS}px;
      font-size: ${({ $token }) => $token.fontSize}px;
    }

    .ant-dropdown-menu-item-divider {
      margin: ${({ $token }) => $token.marginXXS}px 0;
      background: ${({ $token }) => $token.colorBorderSecondary};
    }

    .ant-dropdown-menu-item-group-title {
      padding: ${({ $token }) =>
        `${$token.paddingSM}px ${$token.padding}px ${$token.paddingXS}px`};
      font-size: ${({ $token }) => $token.fontSizeSM - 2}px;
      color: ${({ $token }) => $token.colorTextTertiary};
      font-weight: ${({ $token }) => $token.fontWeightStrong};
    }

    .ant-dropdown-menu-item-group-list {
      margin: 0;
      padding: 0;
    }
  }
`;

const StyledWrapper = styled.span`
  display: inline-block;
`;

const convertToAntdItems = (
  items: DropdownMenuItemType[],
  onItemClick?: (key: string) => void,
) => {
  return items.map((item) => {
    if ('type' in item && item.type === 'divider') {
      return { type: 'divider' as const };
    }

    if ('type' in item && item.type === 'group') {
      return {
        type: 'group' as const,
        label: item.label,
        children: item.children.map((child) => ({
          key: child.key,
          label: child.label,
          icon: child.icon,
          disabled: child.disabled,
          danger: child.danger,
          onClick: () => {
            child.onClick?.();
            onItemClick?.(child.key);
          },
        })),
      };
    }

    return {
      key: item.key,
      label: item.label,
      icon: item.icon,
      disabled: item.disabled,
      danger: item.danger,
      onClick: () => {
        item.onClick?.();
        onItemClick?.(item.key);
      },
    };
  });
};

/**
 * Dropdown component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Dropdown, Button } from '@libertify/ui';
 *
 * const items = [
 *   { key: 'edit', label: 'Edit', icon: <EditOutlined /> },
 *   { key: 'duplicate', label: 'Duplicate' },
 *   { type: 'divider' },
 *   { key: 'delete', label: 'Delete', danger: true },
 * ];
 *
 * <Dropdown items={items} onItemClick={(key) => console.log(key)}>
 *   <Button>Actions</Button>
 * </Dropdown>
 * ```
 */
export const Dropdown = ({
  items,
  children,
  trigger = 'click',
  placement = 'bottomLeft',
  disabled = false,
  onItemClick,
  ...props
}: DropdownProps) => {
  const { token } = theme.useToken();
  const triggerArray = Array.isArray(trigger) ? trigger : [trigger];

  return (
    <>
      <DropdownStyles $token={token} />
      <AntDropdown
        menu={{
          items: convertToAntdItems(items, onItemClick),
          className: 'libertify-dropdown-menu',
        }}
        trigger={triggerArray}
        placement={placement}
        disabled={disabled}
        {...props}
      >
        <StyledWrapper>{children}</StyledWrapper>
      </AntDropdown>
    </>
  );
};

export default Dropdown;
